import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { FileImage, FileText, Printer, Phone, Mail, Globe } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import LogoMark from "@/components/site/LogoMark";
import { BRAND } from "@/lib/brand";

// Visual card preview size. Export at 1.5x scale → 1050x600 px (300 DPI for 3.5" × 2").
const CARD_W = 700;
const CARD_H = 400;
const EXPORT_SCALE = 1.5;

function CardFront() {
  return (
    <div
      data-testid="card-front"
      className="relative bg-[color:var(--navy-950)] text-white overflow-hidden"
      style={{ width: CARD_W, height: CARD_H }}
    >
      <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[color:var(--gold-500)]" />
      <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[color:var(--gold-500)]" />
      <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[color:var(--gold-500)]" />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[color:var(--gold-500)]" />

      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.22), transparent 70%)" }}
      />

      <div className="absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
        <LogoMark size={120} />
        <div
          className="font-display font-black uppercase mt-5"
          style={{ fontSize: 42, lineHeight: 0.9, letterSpacing: "-0.02em" }}
        >
          The Way Forward
        </div>
        <div
          className="font-display uppercase"
          style={{ color: "var(--gold-500)", fontSize: 14, letterSpacing: "0.42em", marginTop: 10 }}
        >
          .solutions
        </div>
        <div className="mt-4" style={{ width: 80, height: 2, background: "var(--gold-500)" }} />
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-secondary)", letterSpacing: "0.08em" }}>
          A Man&apos;s Transformation Guide
        </div>
      </div>
    </div>
  );
}

function CardBack({ qrUrl }) {
  return (
    <div
      data-testid="card-back"
      className="relative bg-[color:var(--navy-950)] text-white overflow-hidden"
      style={{ width: CARD_W, height: CARD_H }}
    >
      <div className="absolute top-0 left-0 right-0" style={{ height: 6, background: "var(--gold-500)" }} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(212,175,55,0.10) 0%, transparent 40%, transparent 60%, rgba(212,175,55,0.06) 100%)",
        }}
      />

      <div className="relative h-full flex">
        <div className="flex-1 pl-10 pr-6 py-9 flex flex-col justify-between">
          <div>
            <div
              className="font-display uppercase"
              style={{ color: "var(--gold-500)", letterSpacing: "0.32em", fontSize: 10 }}
            >
              Founder · Author · Mentor
            </div>
            <div
              className="font-display font-black uppercase mt-2"
              style={{ fontSize: 36, lineHeight: 0.95, letterSpacing: "-0.02em" }}
            >
              Joel M.<br />Alexander
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", letterSpacing: "0.05em", marginTop: 8 }}>
              {BRAND.title}
            </div>
          </div>

          <div style={{ fontSize: 12 }} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Phone size={13} color="var(--gold-500)" />
              <span>{BRAND.phones[0]}</span>
              <span style={{ color: "var(--text-tertiary)" }}>·</span>
              <span style={{ color: "var(--text-secondary)" }}>{BRAND.phones[1]}</span>
            </div>
            <div className="flex items-center gap-2" style={{ wordBreak: "break-all" }}>
              <Mail size={13} color="var(--gold-500)" />
              <span>{BRAND.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe size={13} color="var(--gold-500)" />
              <span>{BRAND.domain}</span>
            </div>
          </div>
        </div>

        <div
          className="flex flex-col items-center justify-center px-6"
          style={{ background: "rgba(255,255,255,0.04)", borderLeft: "1px solid rgba(212,175,55,0.25)", width: 220 }}
        >
          <div style={{ background: "white", padding: 10 }}>
            <QRCodeSVG value={qrUrl} size={140} level="H" includeMargin={false} bgColor="#FFFFFF" fgColor="#040A18" />
          </div>
          <div
            className="font-display uppercase mt-3 text-center"
            style={{ letterSpacing: "0.3em", fontSize: 9, color: "var(--gold-500)" }}
          >
            Scan to visit
          </div>
          <div style={{ fontSize: 10, color: "var(--text-secondary)", marginTop: 3 }}>{BRAND.domain}</div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0" style={{ height: 3, background: "var(--gold-500)" }} />
    </div>
  );
}

export default function BusinessCardPage() {
  const [busy, setBusy] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const qrUrl = BRAND.siteUrl;

  useEffect(() => {
    document.title = `Business Card — ${BRAND.name}`;
  }, []);

  const captureCanvas = async (node) => {
    return await html2canvas(node, {
      backgroundColor: "#040A18",
      scale: EXPORT_SCALE,
      width: CARD_W,
      height: CARD_H,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });
  };

  const downloadPNG = async (side) => {
    setBusy(true);
    try {
      const node = side === "front" ? frontRef.current : backRef.current;
      const canvas = await captureCanvas(node);
      const link = document.createElement("a");
      link.download = `way-forward-business-card-${side}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(`Downloaded ${side} as PNG`);
    } catch (e) {
      toast.error("PNG export failed");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const pdf = new jsPDF({ unit: "in", format: [3.5, 2], orientation: "landscape" });
      const frontCanvas = await captureCanvas(frontRef.current);
      pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, 3.5, 2);
      pdf.addPage([3.5, 2], "landscape");
      const backCanvas = await captureCanvas(backRef.current);
      pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, 3.5, 2);
      pdf.save("way-forward-business-card.pdf");
      toast.success("Downloaded print-ready PDF");
    } catch (e) {
      toast.error("PDF export failed");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="business-card-page">
      <Header />

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[color:var(--gold-500)]" />
          <span className="eyebrow">Printable Business Card</span>
        </div>
        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl">
          Carry the <span className="text-[color:var(--gold-500)]">mission.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[color:var(--text-secondary)] leading-relaxed">
          Print-ready 3.5&quot; × 2&quot; cards (300 DPI). QR scans to{" "}
          <span className="text-[color:var(--text-primary)]">{BRAND.domain}</span>. Download PNGs for digital
          sharing, or the dual-sided PDF for your print shop.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <div>
              <div className="eyebrow mb-3">Front</div>
              <div className="overflow-x-auto border border-[color:var(--border-gold)] bg-[color:var(--navy-900)] p-4 md:p-6 inline-block max-w-full">
                <div ref={frontRef}>
                  <CardFront />
                </div>
              </div>
            </div>

            <div>
              <div className="eyebrow mb-3">Back</div>
              <div className="overflow-x-auto border border-[color:var(--border-gold)] bg-[color:var(--navy-900)] p-4 md:p-6 inline-block max-w-full">
                <div ref={backRef}>
                  <CardBack qrUrl={qrUrl} />
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 bg-[color:var(--navy-900)] border border-[color:var(--border-subtle)] p-6 md:p-8 space-y-6">
              <div>
                <div className="eyebrow mb-2">Export</div>
                <div className="font-display uppercase text-2xl tracking-tight">Download &amp; Print</div>
              </div>

              <div className="space-y-3">
                <button
                  data-testid="download-pdf-btn"
                  onClick={downloadPDF}
                  disabled={busy}
                  className="btn-gold w-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <FileText size={16} /> Download PDF (Print-ready)
                </button>
                <button
                  data-testid="download-front-png-btn"
                  onClick={() => downloadPNG("front")}
                  disabled={busy}
                  className="btn-outline-gold w-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <FileImage size={16} /> Front PNG
                </button>
                <button
                  data-testid="download-back-png-btn"
                  onClick={() => downloadPNG("back")}
                  disabled={busy}
                  className="btn-outline-gold w-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <FileImage size={16} /> Back PNG
                </button>
                <button
                  data-testid="print-btn"
                  onClick={() => window.print()}
                  disabled={busy}
                  className="btn-outline-gold w-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <Printer size={16} /> Print Page
                </button>
              </div>

              <div className="hairline" />

              <div className="space-y-2 text-sm text-[color:var(--text-secondary)] leading-relaxed">
                <div className="eyebrow mb-1">Specs</div>
                <div>3.5&quot; × 2&quot; · 300 DPI · CMYK-safe palette</div>
                <div>
                  QR target:{" "}
                  <span className="text-[color:var(--gold-500)] break-all">{qrUrl}</span>
                </div>
              </div>

              <div className="hairline" />

              <div className="text-xs text-[color:var(--text-tertiary)]">
                Tip: send your printer the PDF for the cleanest result. PNGs are perfect for emails, signatures,
                and social.
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </main>
  );
}
